import React, { useEffect, useState, useMemo } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { compose, graphql } from "lib/apolloClient";
import moment from "moment";

import { CalendarScheduleOutlined, CloseOutlined } from "@toddle-edu/ds-icons";
import {
  Modal,
  DatePicker,
  Button,
  Dropdown,
  DropdownMenu,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import classes from "./DateRangeModal.scss";

import useDateRanges from "./hooks";
import {
  getStudentPostCountQuery,
  getOrganizationAcademicYearsQuery,
  getFilteredPostCountQuery,
  getStudentCoursesQuery,
} from "AppComponents/Post/modules/PostQuery";
import {
  getIsEnableFamilyConsolidatedPortfolio,
  isPortfolioParityEnabled,
  showAcademicYearFilterProfilePortfolio,
  getShouldShowFolderCount,
} from "Post/utils";
import {
  getOrganizationAcademicYearsFromCache,
  getStudentCoursesFromCache,
} from "AppComponents/Post/modules/PostGraphqlHelpers";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getSelectedAcademicYear,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { colors } from "Constants";
import { ArrowContainer, Popover } from "@toddle-edu/react-tiny-popover";
import LoadingComponent from "./LoadingComponent";
import { getUserRemovedCourses } from "Post/utils";

const BLANK_OBJECT = {};
const BLANK_ARRAY = [];
const styles = {
  dropdownContainer: { width: "100%" },
  dropdownTitle: { paddingBottom: "8px" },
  calendarOutlineColor: { color: colors.surfaceDark },
  filterLabelStyles: { color: colors.surfaceDark, fontWeight: 500 },
  closeButtonStyle: { cursor: "pointer" },
  popoverContainerStyle: {
    zIndex: 1000,
    width: "352px",
  },
};

const DateRangeModal = ({
  open,
  academicYearId,
  hasAcademicYear = false,
  dateRangeData,
  activePostCount,
  filters,
  onCloseClick,
  formattedAcademicYears,
  updateFilterObject,
  updateCurrentAcademcYear,
  onDateChange,
  showNewDesign,
  dateFilterRef,
  onClosePopOver,
  isLoading,
  clearFilterOnClose = true,
  setSelectedAcademicYearIds,
  resetAcademicYearRef = null,
  allCourseIds,
  courseIds,
  isSameCourse,
  setCourseId,
  showAyYear = true,
  isCurrentAcademicYearSelected,
  selectedAcademicYearIdForFamilyHomepage,
}) => {
  const { t } = useTranslation(["common", "journal"]);
  const [academicYear, setAcademicYear] = useState();
  const isCurrentAcademicYear =
    (!_.isEmpty(academicYear) && academicYearId === academicYear?.value) ||
    (_.isEmpty(academicYear) && !_.isNil(academicYearId));

  const onFilterValueChange = params => {
    const { fromDate, toDate } = params;
    const filterFromDate = _.get(dateRangeData, "duration.fromDate", null);
    const filterEndDate = _.get(dateRangeData, "duration.toDate", null);
    const isDurationEmpty =
      _.isNull(filterFromDate) &&
      _.isNull(filterEndDate) &&
      (_.isEmpty(fromDate) || _.isNull(fromDate)) &&
      (_.isEmpty(toDate) || _.isNull(toDate));
    onDateChange(prevData => {
      const prevDuration = _.get(prevData, "duration", {});
      return {
        ...prevData,
        ...(isDurationEmpty
          ? { duration: undefined }
          : { duration: { ...prevDuration, ...params } }),
      };
    });
  };

  const [academicYearOptions, academicYearMap] = useMemo(() => {
    const options = [];
    const map = {};

    _.forEach(formattedAcademicYears, academicYear => {
      const label = academicYear?.isPresentAcademicYear
        ? `${academicYear.label} (${t("common:current")})`
        : academicYear.label;

      options.push({
        key: academicYear.key,
        label: label,
      });

      _.set(map, [academicYear.id], { ...academicYear, label });
    });

    return [options, map];
  }, [formattedAcademicYears, t]);

  const { minStartDate, maxStartDate, minEndDate, maxEndDate } = useDateRanges({
    hasAcademicYear,
    academicYear,
    dateRangeData,
    academicYearId,
    academicYearMap,
    isCurrentAcademicYear,
  });

  const updateFilter = ({
    isCurrentAcademicYear,
    resetToCurrentAcademicYear = false,
  }) => {
    const { duration, academicYears } = filters;
    const isDurationEmpty =
      _.isEmpty(duration) ||
      (_.isEmpty(duration.fromDate) && _.isEmpty(duration.toDate));
    let filterObject = {};

    if (isCurrentAcademicYear || resetToCurrentAcademicYear) {
      const selectedAcademicYear = academicYearMap[academicYearId];
      filterObject = {
        duration: {
          fromDate: null,
          toDate: null,
        },
      };
      if (hasAcademicYear) filterObject.academicYears = [academicYearId];
      setAcademicYear({
        label: selectedAcademicYear?.label,
        value: academicYearId,
      });
    } else if (!isCurrentAcademicYear && !_.isEmpty(academicYear?.value)) {
      const selectedAcademicYear = academicYearMap[academicYear?.value];
      filterObject.duration = {
        fromDate: !_.isEmpty(
          _.get(academicYearMap[academicYear?.value], "startDate")
        )
          ? moment(_.get(academicYearMap[academicYear?.value], "startDate"))
          : null,
        toDate: !_.isEmpty(
          _.get(academicYearMap[academicYear?.value], "endDate")
        )
          ? moment(_.get(academicYearMap[academicYear?.value], "endDate"))
          : null,
      };
      if (hasAcademicYear) filterObject.academicYears = [academicYear?.value];
      setAcademicYear({
        label: selectedAcademicYear?.label,
        value: academicYear?.value,
      });
    } else if (!showAyYear && !isCurrentAcademicYear) {
      filterObject.duration = {
        fromDate: !_.isEmpty(
          _.get(
            academicYearMap[selectedAcademicYearIdForFamilyHomepage],
            "startDate"
          )
        )
          ? moment(
              _.get(
                academicYearMap[selectedAcademicYearIdForFamilyHomepage],
                "startDate"
              )
            )
          : null,
        toDate: !_.isEmpty(
          _.get(
            academicYearMap[selectedAcademicYearIdForFamilyHomepage],
            "endDate"
          )
        )
          ? moment(
              _.get(
                academicYearMap[selectedAcademicYearIdForFamilyHomepage],
                "endDate"
              )
            )
          : null,
      };
      if (hasAcademicYear)
        filterObject.academicYears = [selectedAcademicYearIdForFamilyHomepage];
    }
    if (resetToCurrentAcademicYear) return filterObject;
    const academicYearRange = academicYearMap[academicYear?.value];
    const fromDate = _.get(duration, "fromDate", null);
    const toDate = _.get(duration, "toDate", null);
    if (!isDurationEmpty && academicYearRange) {
      const start = moment(academicYearRange.startDate);
      const end = moment(academicYearRange.endDate);
      const isFromDateValid =
        fromDate && moment(fromDate).isBetween(start, end, "day", "[]");
      const isToDateValid =
        toDate && moment(toDate).isBetween(start, end, "day", "[]");
      if (
        (isFromDateValid || _.isEmpty(fromDate)) &&
        (isToDateValid || _.isEmpty(toDate))
      ) {
        filterObject.duration = {
          fromDate,
          toDate,
        };
      }
    }

    return filterObject;
  };

  useEffect(() => {
    if (!showAyYear && !isCurrentAcademicYearSelected) {
      const updatedFilterObjectValue = updateFilter({
        isCurrentAcademicYear: isCurrentAcademicYearSelected,
      });
      updateFilterObject(updatedFilterObjectValue);
      onDateChange(updatedFilterObjectValue);
    }
  }, [open, isCurrentAcademicYearSelected]);

  useEffect(() => {
    const updatedFilterObject = updateFilter({ isCurrentAcademicYear });
    onDateChange(updatedFilterObject);
  }, [
    filters,
    isCurrentAcademicYear,
    academicYear?.value,
    academicYearMap,
    academicYearId,
  ]);

  useEffect(() => {
    updateCurrentAcademcYear?.(academicYearMap?.[academicYearId]);
  }, [academicYearMap, updateCurrentAcademcYear, academicYearId]);

  const handleCloseModal = () => {
    clearFilterOnClose &&
      _.isEmpty(_.compact(_.values(filters?.duration))) &&
      onDateChange({});
    _.isFunction(onCloseClick) && onCloseClick();
  };

  const handleReset = () => {
    const emptyFilters = updateFilter({
      resetToCurrentAcademicYear: true,
    });
    setSelectedAcademicYearIds && setSelectedAcademicYearIds([academicYearId]);
    updateFilterObject(emptyFilters);
    onDateChange(emptyFilters);
    _.isFunction(setCourseId) && setCourseId("ALL");
    // NOTE: This below step will reset filters to blank
    handleCloseModal();
  };

  useEffect(() => {
    if (resetAcademicYearRef) {
      resetAcademicYearRef.current = handleReset;
    }
  }, [resetAcademicYearRef, handleReset]);

  const viewPostText = useMemo(() => {
    if (isLoading) {
      return t("common:loading");
    }
    if (
      _.isNil(_.get(dateRangeData, "duration.fromDate")) &&
      _.isNil(_.get(dateRangeData, "duration.toDate")) &&
      !_.get(filters, "searchText") &&
      !_.get(filters, "folderIds")
    ) {
      return t("journal:view_all_posts");
    }
    if (!_.isEmpty(dateRangeData)) {
      if (activePostCount === 0) {
        return t("journal:view_posts_count", { count: 0 });
      } else if (activePostCount === 1) {
        return t("journal:view_single_post", { count: activePostCount });
      } else if (activePostCount < 999 && activePostCount > 1) {
        return t("journal:view_posts_count", { count: activePostCount });
      } else {
        return t("journal:view_more_posts_count", { count: 999 });
      }
    }
    return t("journal:view_posts");
  }, [activePostCount, isLoading, dateRangeData, filters]);

  const isPresentAcademicYear = useMemo(() => {
    const fromDate = _.get(dateRangeData, "duration.fromDate", null);
    const toDate = _.get(dateRangeData, "duration.toDate", null);
    const today = moment();
    if (hasAcademicYear) {
      return (
        academicYearId === academicYear?.value &&
        _.isNull(fromDate) &&
        _.isNull(toDate)
      );
    } else {
      return _.isNull(fromDate) && _.isNull(toDate);
    }
  }, [
    academicYearId,
    hasAcademicYear,
    academicYearMap,
    dateRangeData,
    academicYear,
  ]);

  const handleViewPost = () => {
    const updatedCourseIds = isSameCourse ? courseIds : allCourseIds;
    setSelectedAcademicYearIds &&
      setSelectedAcademicYearIds([_.get(academicYear, "value")]);
    updateFilterObject({
      ...dateRangeData,
      courseIds: updatedCourseIds,
    });
    if (!isSameCourse) {
      _.isFunction(setCourseId) && setCourseId("ALL");
    }
    handleCloseModal();
  };

  const onAcademicYearSelection = selectedAcademicYear => {
    const academicYearLabel = _.get(
      academicYearMap[_.get(selectedAcademicYear, "key")],
      "label"
    );
    const currentDate = moment();
    setAcademicYear({
      value: _.get(selectedAcademicYear, "key"),
      label: academicYearLabel,
    });

    const selectedAcademicYearData = {
      duration: undefined,
      academicYears: [_.get(selectedAcademicYear, "key")],
    };
    onDateChange(selectedAcademicYearData);
  };

  const onStartDateChange = value => {
    const isDurationEmpty =
      _.isEmpty(dateRangeData.duration) ||
      (_.isNull(_.get(dateRangeData, "duration.fromDate", null)) &&
        _.isNull(_.get(dateRangeData, "duration.toDate", null)));
    if (isDurationEmpty) {
      onFilterValueChange({ fromDate: value, toDate: value });
    } else {
      onFilterValueChange({ fromDate: value });
    }
  };
  const onEndDateChange = value => {
    onFilterValueChange({ toDate: value });
  };

  const stopPropagation = e => e.stopPropagation();

  const getContent = ({ position, childRect, popoverRect }) => {
    return (
      <div onClick={stopPropagation}>
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={colors.white}
          arrowSize={10}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <div className={classes.popUpWrapper}>
            <div className={classes.popUpHeader}>
              <h6>{t("journal:select_date_range")}</h6>
              <CloseOutlined
                style={styles.closeButtonStyle}
                onClick={onClosePopOver}
                variant="subtle"
              />
            </div>
            <div className={classes.containerV2}>
              {hasAcademicYear && (
                <div style={styles.dropdownContainer}>
                  <div style={styles.dropdownTitle} className="text-label-2">
                    {t("common:academic_year")}
                  </div>
                  <Dropdown
                    dsVersion="2.0"
                    className={classes.dropdown}
                    overlay={
                      <DropdownMenu
                        dsVersion="2.0"
                        showSelection
                        selectionType="tick"
                        value={academicYear?.value}
                        options={academicYearOptions}
                        onClick={onAcademicYearSelection}
                      />
                    }
                  >
                    <FilterLabelButton
                      icon={
                        <CalendarScheduleOutlined
                          style={styles.calendarOutlineColor}
                        />
                      }
                      variant="outlined-subtle"
                      size="medium"
                      labelStyle={styles.filterLabelStyles}
                      labelValue={_.get(academicYear, "label")}
                    />
                  </Dropdown>
                </div>
              )}
              <div className={classes.datePickerContainer}>
                <div className="text-label">{t("common:start_date")}</div>
                <DatePicker
                  dsVersion="2.0"
                  picker="date"
                  size="medium"
                  isClearable={true}
                  minDate={minStartDate}
                  maxDate={maxStartDate}
                  value={_.get(dateRangeData, "duration.fromDate", null)}
                  placeholder={t("common:select_date")}
                  onChange={onStartDateChange}
                />
              </div>
              <div className={classes.datePickerContainer}>
                <div className="text-label">{t("common:end_date")}</div>
                <DatePicker
                  dsVersion="2.0"
                  picker="date"
                  size="medium"
                  isClearable={true}
                  maxDate={maxEndDate}
                  minDate={minEndDate}
                  value={_.get(dateRangeData, "duration.toDate", null)}
                  placeholder={t("common:select_date")}
                  onChange={onEndDateChange}
                />
              </div>
            </div>
            <div className={classes.modalFooter}>
              {!isPresentAcademicYear && (
                <Button
                  dsVersion="2.0"
                  variant="progressive"
                  type="inline"
                  onClick={handleReset}
                >
                  {t("reset")}
                </Button>
              )}
              <div className={classes.primaryButtons}>
                <Button
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  onClick={handleCloseModal}
                >
                  {t("cancel")}
                </Button>
                <Button
                  icon={isLoading ? <LoadingComponent /> : undefined}
                  disabled={isLoading || !activePostCount}
                  dsVersion="2.0"
                  variant="primary"
                  onClick={handleViewPost}
                >
                  {viewPostText}
                </Button>
              </div>
            </div>
          </div>
        </ArrowContainer>
      </div>
    );
  };

  if (showNewDesign) {
    return (
      <Popover
        isOpen={open}
        align="end"
        boundaryInset={50}
        positions={["bottom"]}
        containerStyle={styles.popoverContainerStyle}
        onClickOutside={onClosePopOver}
        reposition={true}
        content={getContent}
        childrenRef={dateFilterRef}
        isChildrenRefPassed={true}
      />
    );
  }

  return (
    <Modal
      dsVersion="2.0"
      onClose={handleCloseModal}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      hasOverlay={true}
      width="352px"
      isOpen={open}
      height={hasAcademicYear && showAyYear ? "426px" : "304px"}
    >
      <Modal.Header title={t("journal:select_date_range")} isClosable={true} />
      <Modal.Body isScrollable={true} style={{ paddingBlock: "0px" }}>
        <div className={classes.container}>
          {hasAcademicYear && showAyYear && (
            <div style={styles.dropdownContainer}>
              <div style={styles.dropdownTitle} className="text-label">
                {t("common:academic_year")}
              </div>
              <Dropdown
                dsVersion="2.0"
                className={classes.dropdown}
                overlay={
                  <DropdownMenu
                    dsVersion="2.0"
                    showSelection
                    selectionType="tick"
                    value={academicYear?.value}
                    options={academicYearOptions}
                    onClick={onAcademicYearSelection}
                  />
                }
              >
                <FilterLabelButton
                  icon={
                    <CalendarScheduleOutlined
                      style={styles.calendarOutlineColor}
                    />
                  }
                  variant="outlined-subtle"
                  size="medium"
                  labelStyle={styles.filterLabelStyles}
                  labelValue={_.get(academicYear, "label")}
                />
              </Dropdown>
            </div>
          )}
          <div className={classes.datePickerContainer}>
            <div className="text-label">{t("common:start_date")}</div>
            <DatePicker
              picker="date"
              size="medium"
              isClearable={true}
              minDate={minStartDate}
              maxDate={maxStartDate}
              value={_.get(dateRangeData, "duration.fromDate", null)}
              // label={t("common:start_date")}
              placeholder={t("common:select_date")}
              onChange={onStartDateChange}
            />
          </div>
          <div className={classes.datePickerContainer}>
            <div className="text-label">{t("common:end_date")}</div>
            <DatePicker
              picker="date"
              size="medium"
              isClearable={true}
              maxDate={maxEndDate}
              minDate={minEndDate}
              value={_.get(dateRangeData, "duration.toDate", null)}
              placeholder={t("common:select_date")}
              onChange={onEndDateChange}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.modalFooter}>
          {!isPresentAcademicYear && (
            <Button
              dsVersion="2.0"
              variant="progressive"
              type="inline"
              onClick={handleReset}
            >
              {t("reset")}
            </Button>
          )}
          <div className={classes.primaryButtons}>
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              onClick={handleCloseModal}
            >
              {t("cancel")}
            </Button>
            <Button dsVersion="2.0" variant="primary" onClick={handleViewPost}>
              {viewPostText}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const getOrganizationAcademicYears = _.memoize(
  ({ orgId, curriculumProgramId }) => {
    return {
      id: orgId,
      termsFilters: {
        curriculumProgramIds: curriculumProgramId
          ? [curriculumProgramId]
          : undefined,
      },
    };
  },
  ({ orgId, curriculumProgramId }) => `${orgId}_${curriculumProgramId}`
);
const formatCurrentActiveTerms = _.memoize(
  ({ allAcademicYears, currentAcademicYear }) => {
    const getFormatDate = date => moment(date).format("YYYY");
    return _.map(allAcademicYears, term => {
      const isPresentAcademicYear = currentAcademicYear?.[0]?.id === term?.id;
      return {
        ...term,
        label: `${getFormatDate(term.startDate)} - ${getFormatDate(
          term.endDate
        )}`,
        key: term.id,
        isPresentAcademicYear,
      };
    });
  },
  ({ allAcademicYears, currentAcademicYear }) => {
    const yearsKey = _.map(allAcademicYears, "id").join("|");
    const currentYearKey = currentAcademicYear?.[0]?.id;
    return `${yearsKey}_current:${currentYearKey}`;
  }
);

// NOTE: Need this for future ref if we are using count from postCount
// const getActivePostCount = _.memoize(({ allPostCount, postState }) => {
//   switch (postState) {
//     case "PUBLISHED":
//       return _.get(allPostCount, "published", 0);
//     case "DRAFT":
//       return _.get(allPostCount, "draft", 0);
//     case "IN_APPROVAL":
//       return _.get(allPostCount, "inApproval", 0);
//     default:
//       break;
//   }
// }, JSON.stringify);

const feedTypeDefaultValue = ["PORTFOLIO"];

const getAllCourseIdsMemoized = _.memoize(
  ({ courseMap, isSameCourse }) => {
    const filteredCourses = !isSameCourse
      ? courseMap
      : _.filter(
          courseMap,
          course =>
            _.includes(_.get(course, "course.enabledModules"), "PORTFOLIO") &&
            course
        );

    return _.map(filteredCourses, course => ({
      ..._.get(course, "course", {}),
      isUserRemovedFromCourse: _.get(course, "isUserRemovedFromCourse"),
    }));
  },
  ({ courseMap }) =>
    _.chain(courseMap)
      .map(course => _.get(course, "course.id"))
      .sortBy()
      .join(",")
      .value()
);

const getFormattedDuration = duration => {
  if (!duration) return undefined;

  return {
    fromDate: duration.fromDate || null,
    toDate: duration.toDate
      ? moment(duration.toDate).endOf("day").toISOString()
      : null,
  };
};

const mapStateToProps = (state, ownProps) => {
  const journalFeedFilters = _.get(
    ownProps,
    "journalFeedFilters",
    BLANK_OBJECT
  );
  const dateRangeData = _.get(ownProps, "dateRangeData", BLANK_OBJECT);
  const filters = _.get(ownProps, "filters", BLANK_OBJECT);
  const courseIds = _.get(journalFeedFilters, "courseIds", BLANK_ARRAY);
  const { studentId } = ownProps;
  const userInfo = getUserInfo();
  const courseId = state.teacher.selected_class.selected_course;
  const userType = state.login.userInfo.user_type;
  const curriculumProgramId = getSelectedCurriculumId();
  const organizationAcademicYearsVariables = getOrganizationAcademicYears({
    orgId: userInfo.org_id,
    curriculumProgramId,
  });
  const userId = userInfo.id;
  const currentCurriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );
  const academicYearId = getSelectedAcademicYearId(state);
  const selectedAcademicYearId = _.get(
    ownProps,
    "academicYearId",
    academicYearId
  );
  const selectedAcademicYear = getSelectedAcademicYear(state);
  const isCurrentAcademicYearSelected =
    selectedAcademicYear?.isCurrentAcademicYear;
  const selectedAcademicYearEndDate = selectedAcademicYear?.endDate;
  const selectedAcademicYearStartDate = selectedAcademicYear?.startDate;
  const selectedAcademicYearIds = _.get(dateRangeData, "academicYears", []);
  const selectedAcademicYearIdForFamilyHomepage = selectedAcademicYear?.id;

  const moduleFilters = {
    moduleNames: ["PORTFOLIO"],
    userType: "student",
  };

  const aclControlParameterFilter = {
    controlParameters: "Module:Portfolio",
  };
  const queryOptions = {
    variables: {
      studentId: studentId,
      filters: {
        academicYearIds:
          selectedAcademicYearIds.length > 0
            ? selectedAcademicYearIds
            : selectedAcademicYearId
            ? [selectedAcademicYearId]
            : undefined,
        curriculumProgramIds: [currentCurriculumProgramId],
        includeRemovedCourses: getUserRemovedCourses(),
      },
      enabledModulesFilters: moduleFilters,
      aclControlParameterFilter,
      ...(getShouldShowFolderCount() && {
        postFilters: {
          orderBy: "PUBLISHED_AT",
          studentIds: [studentId],
          states: ["PUBLISHED"],
          duration: getFormattedDuration(ownProps.filters?.duration),
        },
      }),
    },
  };
  const restFilters = _.omit(filters, "folderIds");
  const updatedAcademicFilters = {
    ...journalFeedFilters,
    ...restFilters,
    ...dateRangeData,
    duration: {
      fromDate: !_.isNull(_.get(dateRangeData, "duration.fromDate", null))
        ? moment(
            dateRangeData?.duration?.fromDate.format("YYYY-MM-DD")
          ).toISOString()
        : null,
      toDate: !_.isNull(_.get(dateRangeData, "duration.toDate", null))
        ? moment(dateRangeData?.duration?.toDate.format("YYYY-MM-DD"))
            .add({ hours: 23, minutes: 59 })
            .toISOString()
        : null,
    },
  };

  const currentAcademicYearId =
    selectedAcademicYearIds.length > 0
      ? selectedAcademicYearIds?.[0]
      : selectedAcademicYearId;
  return {
    studentId,
    courseIds,
    courseId,
    organizationAcademicYearsVariables,
    userType,
    hasAcademicYear: ownProps?.hasAcademicYear,
    userId,
    updatedAcademicFilters,
    queryOptions,
    dateRangeData,
    isSameCourse:
      selectedAcademicYearId ===
      (currentAcademicYearId || _.get(journalFeedFilters, "academicYears.[0]")),
    feedType: _.get(ownProps, "feedType", feedTypeDefaultValue),
    academicYearId: academicYearId,
    isCurrentAcademicYearSelected,
    selectedAcademicYearEndDate,
    selectedAcademicYearStartDate,
    selectedAcademicYearIdForFamilyHomepage,
  };
};

const getUpdatedFiltersObject = (filters, userId) => {
  const updatedFilters = { ...filters };
  if (updatedFilters.state === "IN_APPROVAL_STAFF") {
    updatedFilters.states = ["IN_APPROVAL", "REJECTED"];
    updatedFilters.createdBy = userId;
    delete updatedFilters.state;
  }
  return updatedFilters;
};

export default compose(
  connect(mapStateToProps),
  graphql(getOrganizationAcademicYearsQuery, {
    name: "getOrganizationAcademicYears",
    skip: () => !isPortfolioParityEnabled(),
    options: ({ organizationAcademicYearsVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: organizationAcademicYearsVariables,
    }),
    props({ ownProps: { organizationAcademicYearsVariables } }) {
      const data = getOrganizationAcademicYearsFromCache(
        organizationAcademicYearsVariables
      );
      const allAcademicYears = _.get(data, "node.academicYears", BLANK_ARRAY);
      const currentAcademicYear = _.filter(
        allAcademicYears,
        item => item?.isCurrentAcademicYear
      );

      const formattedAcademicYears = formatCurrentActiveTerms({
        allAcademicYears,
        currentAcademicYear,
      });

      return {
        allAcademicYears,
        formattedAcademicYears,
      };
    },
  }),
  graphql(getStudentCoursesQuery, {
    name: "getStudentCourses",
    skip: ({ studentId, queryOptions }) =>
      !(
        showAcademicYearFilterProfilePortfolio() ||
        getIsEnableFamilyConsolidatedPortfolio()
      ) ||
      !(
        _.includes(window.location.href, "/studentFeed") ||
        _.includes(window.location.href, "/family-portfolio-summary")
      ) ||
      _.isEmpty(studentId) ||
      _.isEmpty(queryOptions?.variables?.filters?.academicYearIds),
    options: ({ queryOptions }) => ({
      fetchPolicy: "cache-and-network",
      variables: queryOptions.variables,
      context: { useHttpLink: true },
    }),
    props({
      getStudentCourses,
      ownProps: { isData, isSameCourse, isLoading },
    }) {
      const studentDetails = getStudentCoursesFromCache(
        getStudentCourses.variables
      );

      const courseMap = _.get(studentDetails, "courseMap", BLANK_ARRAY);
      const courses = getAllCourseIdsMemoized({ courseMap, isSameCourse });
      const courseIds = _.map(courses, "id", BLANK_ARRAY);
      return {
        allCourseIds: courseIds,
        isData: !_.isEmpty(studentDetails) && isData,
        isLoading:
          _.includes([1, 2], getStudentCourses.networkStatus) || isLoading,
      };
    },
  }),
  graphql(getFilteredPostCountQuery, {
    name: "getFilteredPostCount",
    skip: ({ dateRangeData, courseId, hasAcademicYear }) => {
      return _.isEmpty(courseId) || hasAcademicYear;
    },
    options: ({
      courseId,
      filters,
      userType,
      dateRangeData,
      userId,
      feedType,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: courseId,
        folderIds: filters.folderIds,
        ...(_.includes(["student", "parent"], userType)
          ? {
              isShowHiddenFromStudents: _.isEqual(userType, "parent"),
            }
          : BLANK_OBJECT),
        filters: {
          ...getUpdatedFiltersObject(filters, userId),
          creatorType:
            userType === "staff" && filters?.state === "IN_APPROVAL_STAFF"
              ? "staff"
              : filters?.state === "IN_APPROVAL"
              ? "student"
              : undefined,
          duration: {
            fromDate: !_.isNull(_.get(dateRangeData, "duration.fromDate", null))
              ? moment(
                  dateRangeData?.duration?.fromDate.format("YYYY-MM-DD")
                ).toISOString()
              : null,
            toDate: !_.isNull(_.get(dateRangeData, "duration.toDate", null))
              ? moment(dateRangeData?.duration?.toDate.format("YYYY-MM-DD"))
                  .add({ hours: 23, minutes: 59 })
                  .toISOString()
              : null,
          },
          ...(_.includes(feedType, "ROUTINE") &&
          _.isEqual(filters.state, "PUBLISHED")
            ? { state: undefined, states: ["PUBLISHED", "SCHEDULED"] }
            : {}),
          orderBy: _.get(filters, "orderBy", "PUBLISHED_AT"),
          feedTypes: feedType ? feedType : feedTypeDefaultValue,
        },
        postCountFilters: {
          feedTypes: feedType ? feedType : feedTypeDefaultValue,
          postSearchText: _.get(filters, "searchText"),
          duration: {
            fromDate: !_.isNull(_.get(dateRangeData, "duration.fromDate", null))
              ? moment(
                  dateRangeData?.duration?.fromDate.format("YYYY-MM-DD")
                ).toISOString()
              : null,
            toDate: !_.isNull(_.get(dateRangeData, "duration.toDate", null))
              ? moment(dateRangeData?.duration?.toDate.format("YYYY-MM-DD"))
                  .add({ hours: 23, minutes: 59 })
                  .toISOString()
              : null,
          },
          orderBy: _.get(filters, "orderBy", "PUBLISHED_AT"),
        },
      },
    }),
    props({ getFilteredPostCount, ownProps: { filters } }) {
      const allPostCount = _.get(getFilteredPostCount, "node.posts.totalCount");
      return {
        activePostCount: allPostCount,
        isLoading: _.isNil(allPostCount),
      };
    },
  }),
  graphql(getStudentPostCountQuery, {
    name: "getStudentPostCount",
    skip: ({ hasAcademicYear, studentId }) => !hasAcademicYear || !studentId,
    options: ({
      updatedAcademicFilters,
      studentId,
      allCourseIds,
      courseIds,
      isSameCourse,
      feedType,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        studentId,
        filters: {
          ...updatedAcademicFilters,
          feedTypes: feedType,
          courseIds: isSameCourse ? courseIds : allCourseIds,
        },
      },
    }),
    props({ getStudentPostCount }) {
      const activePostCount = _.get(
        getStudentPostCount,
        "node.posts.totalCount",
        0
      );
      return {
        activePostCount,
      };
    },
  })
)(DateRangeModal);
